// Financial Costpoint Management Service Types

export interface VATRate {
  id: number;
  name: string; // e.g., "General VAT", "Reduced VAT - Food"
  rate: number; // VAT rate percentage (25.5, 24.0, etc.)
  start_date: string; // When this rate became effective
  end_date: string | null; // When this rate ended (null for current rate)
  description?: string; // Optional description
  created_at: string;
  updated_at: string;
}

export interface Transaction {
  id: number;
  date: string;
  original_date?: string; // Original date from bank statement
  heading?: string; // User-editable transaction title
  original_heading?: string; // Original heading/title from bank statement
  description?: string; // User-editable notes/comments
  amount: number; // Gross amount (what bank shows)
  type: 'deposit' | 'payment';
  // VAT fields
  has_vat: boolean; // Whether this transaction includes VAT
  vat_id: number | null; // Reference to VAT rate used for this transaction
  vat_rate?: VATRate; // Populated VAT rate details when joined
  net_amount: number; // Amount without VAT for budget comparisons
  vat_amount: number; // VAT portion of the transaction
  costpoints: CostpointAssignment[];
  tags: Tag[];
  import_batch_id?: string;
  created_at: string;
  updated_at: string;
}

// Response envelope for GET /api/v1/transactions (paginated + optionally year/month filtered).
export interface PaginatedTransactions {
  data: Transaction[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CostpointAssignment {
  id: number;
  transaction_id: number;
  costpoint: CostpointType;
  amount: number;
  percentage: number;
  tags: Tag[];
  created_at: string;
}

export type CostpointType = 'cs' | 'pubg' | 'admin' | 'other' | 'UNASSIGNED';

export interface Budget {
  id: number;
  costpoint: CostpointType;
  period_start: string;
  period_end: string;
  amount: number;
  created_at: string;
  updated_at: string;
}

// Enhanced Budget System Types
export type BudgetPeriodType = 'H1' | 'H2' | 'ANNUAL';
export type BudgetPeriodStatus = 'draft' | 'active' | 'locked' | 'archived';

export interface BudgetPeriod {
  id: number;
  period_type: BudgetPeriodType;
  year: number;
  start_date: string;
  end_date: string;
  status: BudgetPeriodStatus;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface BudgetAllocation {
  id: number;
  budget_period_id: number;
  costpoint: CostpointType;
  allocated_amount: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface BudgetAdjustment {
  id: number;
  budget_allocation_id: number;
  adjustment_amount: number;
  adjustment_reason: string;
  adjusted_by: string;
  adjusted_at: string;
}

// Commitments: known-but-not-yet-transacted expenses/income (see docs/11 discussion on
// locked-budget adjustments). Independent of BudgetAdjustment above - a commitment never
// edits the budget allocation itself, so it stays usable even on a locked budget period.
// Counts as budgeted money everywhere (Forecast, Reports) - summed into budgeted_cost/
// budgeted_revenue per costpoint right alongside budget_items. No status/lifecycle: a
// commitment is just edited or deleted when it's no longer relevant.
export type CommitmentType = 'income' | 'expenses';

export interface Commitment {
  id: number;
  budget_period_id: number;
  costpoint: CostpointType;
  type: CommitmentType;
  amount: number;
  description: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface BudgetPeriodWithAllocations extends BudgetPeriod {
  allocations: BudgetAllocation[];
  total_allocated: number;
}

export interface BudgetActualComparison {
  costpoint: CostpointType;
  allocated_amount: number;
  actual_amount: number;
  variance: number;
  variance_percentage: number;
}

export interface BudgetPeriodReport {
  period: BudgetPeriod;
  comparisons: BudgetActualComparison[];
  total_allocated: number;
  total_actual: number;
  total_variance: number;
  performance_status: 'under_budget' | 'over_budget' | 'on_target';
}

export interface BudgetDashboard {
  current_period: BudgetPeriod | null;
  progress_percentage: number;
  spend_percentage: number;
  status: 'on_track' | 'warning' | 'over_budget';
  days_remaining: number;
  budget_remaining: number;
  daily_budget_remaining: number;
  projected_end_balance: number;
  costpoint_alerts: BudgetAlert[];
}

export interface BudgetAlert {
  costpoint: CostpointType;
  alert_type: 'approaching_limit' | 'exceeded' | 'under_spending';
  percentage_used: number;
  recommended_action: string;
}

export interface LegacyBudgetPeriod {
  start: string;
  end: string;
  duration_months: number;
}

export interface FinancialReport {
  period: LegacyBudgetPeriod;
  income: number;
  expenses: number;
  net: number;
  costpoint_breakdown: CostpointSummary[];
  budget_variance: BudgetVariance[];
}

export interface CostpointSummary {
  costpoint: CostpointType;
  total_amount: number;
  transaction_count: number;
  budget_amount?: number;
  variance?: number;
}

export interface BudgetVariance {
  costpoint: CostpointType;
  budgeted: number;
  actual: number;
  variance: number;
  variance_percentage: number;
}

export interface CSVImportResult {
  success: boolean;
  imported_count: number;
  errors: string[];
  transactions: Transaction[];
}

// Draft-related types for Redis workflow
export interface ImportDraft {
  id: string;
  user_id: string;
  filename: string;
  status: 'draft' | 'validating' | 'ready' | 'committed' | 'abandoned';
  transactions: DraftTransaction[];
  created_at: string;
  updated_at: string;
  validation_summary: ValidationSummary;
}

export interface DraftTransaction {
  id: string;
  date: string;
  original_date?: string; // Original date from bank statement
  heading?: string; // User-editable transaction title
  original_heading?: string; // Original heading/title from bank statement
  description?: string; // User-editable notes/comments
  amount: number; // Gross amount (what bank shows)
  type: 'deposit' | 'payment';
  // VAT fields
  has_vat: boolean; // Whether this transaction includes VAT
  vat_id: number | null; // Reference to VAT rate used for this transaction
  vat_rate?: VATRate; // Populated VAT rate details for display
  net_amount: number; // Amount without VAT for budget comparisons
  vat_amount: number; // VAT portion of the transaction
  costpoints: DraftCostpointAssignment[];
  tags: Tag[];
  validation_errors: string[];
  is_complete: boolean;
}

export interface DraftCostpointAssignment {
  costpoint: CostpointType;
  amount: number;
  percentage: number;
  tags: Tag[];
}

export interface ValidationSummary {
  total_transactions: number;
  complete_transactions: number;
  incomplete_transactions: number;
  total_errors: number;
}

export interface VatAssignment {
  has_vat: boolean;
  vat_id: number | null;
}

export interface SplitTransactionRequest {
  split_amounts: number[];
  vat_assignments: VatAssignment[];
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

// Enhanced Costpoint and Tagging System Types
export interface Tag {
  id: number;
  name: string; // lowercase normalized
  display_name: string; // original case preserved
  usage_count: number;
  created_at: string;
}

export interface ImportBatch {
  id: string;
  filename: string;
  user_id: string;
  imported_at: string;
  last_edited_at: string;
  original_transaction_count: number;
  current_transaction_count: number;
  status: 'active' | 'archived' | 'deleted';
  metadata?: Record<string, unknown>;
}

// Enhanced transaction splitting interfaces
export interface CostpointSplit {
  costpoint: CostpointType;
  amount: number;
  percentage: number;
  tags: Tag[];
}

export interface EnhancedTransaction {
  id: string;
  description: string;
  amount: number;
  date: string;
  splits: CostpointSplit[];
  import_batch_id?: string;
  global_tags?: Tag[]; // Tags applied to entire transaction
}

// Enhanced draft types with tags and splitting
export interface EnhancedDraftTransaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  type: 'deposit' | 'payment';
  splits: EnhancedDraftCostpointAssignment[];
  global_tags: Tag[];
  validation_errors: string[];
  is_complete: boolean;
}

export interface EnhancedDraftCostpointAssignment {
  costpoint: CostpointType;
  amount: number;
  percentage: number;
  tags: Tag[];
}

// Tag management interfaces
export interface TagSearchResult {
  id: number;
  name: string;
  display_name: string;
  usage_count: number;
}

export interface TagAutocompleteOption {
  value: string;
  label: string;
  usage_count?: number;
  is_new?: boolean;
}

export interface TagOperationRequest {
  transaction_id: number;
  tag_ids: number[];
  operation: 'add' | 'remove' | 'replace';
}

export interface BulkTagOperationRequest {
  transaction_ids: number[];
  tag_ids: number[];
  operation: 'add' | 'remove' | 'replace';
}

// Import batch management interfaces
export interface ImportBatchSummary {
  id: string;
  filename: string;
  imported_at: string;
  transaction_count: number;
  status: 'active' | 'archived' | 'deleted';
  last_edited_at: string;
}

export interface ImportBatchEditRequest {
  transactions: EnhancedTransaction[];
  validate_before_save: boolean;
}

export interface ImportBatchValidationResult {
  is_valid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  transaction_count: number;
}

export interface ValidationError {
  transaction_id: string;
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface ValidationWarning {
  transaction_id: string;
  field: string;
  message: string;
  suggestion?: string;
}

// Financial forecast types (M1: org-wide fiscal year balances)
export interface FiscalYearBalance {
  id: number | null; // null when no row exists yet for the year (defaults returned)
  year: number;
  opening_balance: number;
  current_balance_override: number | null;
  current_balance_note: string | null;
  current_balance_updated_at: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// Financial forecast types (M2: per-costpoint + org-wide year-end projection)
export type ForecastStatus = 'on_track' | 'at_risk' | 'exceeded';

// H1/H2/ALL period scoping for GET /api/v1/forecast (see ForecastReport.period_type below).
// Deliberately only these three - no standalone 'ANNUAL' filter, since 'ALL' already covers
// the whole-year view regardless of how budget_periods happens to be split.
export type ForecastPeriodType = 'H1' | 'H2' | 'ALL';

export interface ForecastCostpointBreakdown {
  costpoint: CostpointType;
  actual_cost_to_date: number;
  actual_revenue_to_date: number;
  // Includes commitments (see Commitment) summed in alongside budget_items - a commitment
  // counts as budgeted money, not a separate bucket.
  budgeted_cost: number;
  budgeted_revenue: number;
  remaining_budgeted_cost: number;
  remaining_budgeted_revenue: number;
  projected_year_end_cost: number;
  projected_year_end_revenue: number;
  projected_net: number; // projected_year_end_revenue - projected_year_end_cost
  cost_status: ForecastStatus;
  revenue_status: ForecastStatus; // "shortfall" equivalent of cost_status
}

export interface ForecastReport {
  year: number;
  period_type: ForecastPeriodType; // which slice of the year this report is scoped to
  as_of_date: string; // YYYY-MM-DD, the date D the forecast was computed as of
  opening_balance: number; // from fiscal_year_balances (0 default when no row yet)
  current_balance_override: number | null; // raw reconciliation override, if set
  current_balance_note: string | null;
  current_balance_updated_at: string | null;
  org_current_computed_balance: number; // opening_balance + actuals-to-date across all costpoints
  org_projected_net: number; // opening_balance + sum of costpoint projected_net
  org_cost_status: ForecastStatus;
  org_revenue_status: ForecastStatus;
  org_status: ForecastStatus; // worst-of cost/revenue status, org-wide
  costpoint_breakdown: ForecastCostpointBreakdown[];
}

// VAT calculation utilities

export const calculateNetAmount = (
  grossAmount: number,
  hasVat: boolean,
  vatRate: number
): number => {
  if (!hasVat) return grossAmount;
  return Math.round(((grossAmount * 100) / (100 + vatRate)) * 100) / 100;
};

export const calculateVatAmount = (
  grossAmount: number,
  hasVat: boolean,
  vatRate: number
): number => {
  if (!hasVat) return 0;
  const netAmount = calculateNetAmount(grossAmount, hasVat, vatRate);
  return Math.round((grossAmount - netAmount) * 100) / 100;
};
